import React from "react";
import {
  Box,
  Typography,
  Link,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

const ZONES = [
  { label: "IST", tz: "Asia/Kolkata" },
  { label: "EDT", tz: "America/New_York" },
  { label: "CEST", tz: "Europe/Berlin" },
];

function formatRange(startsAt, endsAt, tz) {
  if (!startsAt || !endsAt) return "—";
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${fmt.format(new Date(startsAt))} – ${fmt.format(new Date(endsAt))}`;
}

// Two interlocking gears rotating in opposite directions — the classic
// "under maintenance" motif, done with CSS keyframes (no library).
function AnimatedGears({ brand }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: 132,
        height: 108,
        mx: "auto",
        mb: 4.5,
        "@keyframes spinCW": {
          from: { transform: "rotate(0deg)" },
          to: { transform: "rotate(360deg)" },
        },
        "@keyframes spinCCW": {
          from: { transform: "rotate(0deg)" },
          to: { transform: "rotate(-360deg)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& .gear": { animation: "none !important" },
        },
      }}
    >
      <SettingsIcon
        className="gear"
        sx={{
          position: "absolute",
          top: 0,
          left: 8,
          fontSize: 78,
          color: brand,
          animation: "spinCW 8s linear infinite",
        }}
      />
      <SettingsIcon
        className="gear"
        sx={{
          position: "absolute",
          bottom: 0,
          right: 6,
          fontSize: 56,
          color: brand,
          opacity: 0.55,
          animation: "spinCCW 6s linear infinite",
        }}
      />
    </Box>
  );
}

// Full-screen maintenance cover. Presentational only — MaintenanceGate decides
// when to render it. Centered, whitespace-heavy layout in the app theme.
function MaintenanceScreen({
  title = "Scheduled maintenance",
  message = "We're undergoing scheduled maintenance. The platform will be back shortly.",
  startsAt = null,
  endsAt = null,
  contactEmail = "connect@solsphere.ai",
}) {
  const theme = useTheme();
  const c = theme.customColors;
  const brand = c.icons.dark; // #031B89

  const hasWindow = Boolean(startsAt && endsAt);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: theme.zIndex.modal + 1,
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        py: 6,
        overflow: "auto",
        backgroundImage: `radial-gradient(1100px 460px at 50% -140px, ${brand}12, transparent)`,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 600, textAlign: "center" }}>
        {/* Animated gears */}
        <AnimatedGears brand={brand} />

        {/* Title */}
        <Typography
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            fontSize: { xs: "26px", sm: "32px" },
            lineHeight: 1.2,
            color: c.text.strong,
            mb: 2,
          }}
        >
          {title}
        </Typography>

        {/* Message */}
        <Typography
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: "16px",
            lineHeight: 1.7,
            color: c.text.secondary,
            maxWidth: 460,
            mx: "auto",
          }}
        >
          {message}
        </Typography>

        {hasWindow && (
          <>
            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                color: c.text.disabled,
                mt: 5,
                mb: 1.5,
              }}
            >
              Maintenance window
            </Typography>

            <Box
              sx={{
                maxWidth: 520,
                mx: "auto",
                border: `1px solid ${c.border.subtle}`,
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 460 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: c.background.cardHeader }}>
                      {ZONES.map((z) => (
                        <TableCell
                          key={z.label}
                          align="center"
                          sx={{
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 600,
                            fontSize: "12px",
                            letterSpacing: "0.4px",
                            textTransform: "uppercase",
                            color: c.text.secondary,
                            borderColor: c.border.subtle,
                            py: 1.5,
                          }}
                        >
                          {z.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ "& td": { border: 0 } }}>
                      {ZONES.map((z) => (
                        <TableCell
                          key={z.label}
                          align="center"
                          sx={{
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: "15px",
                            fontWeight: 600,
                            color: c.text.strong,
                            whiteSpace: "nowrap",
                            py: 2,
                          }}
                        >
                          {formatRange(startsAt, endsAt, z.tz)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </>
        )}

        {/* Contact */}
        {contactEmail && (
          <Typography
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
              color: c.text.secondary,
              mt: 5,
            }}
          >
            Need help? Contact us at{" "}
            <Link
              href={`mailto:${contactEmail}`}
              sx={{
                color: c.status.info.text,
                textDecoration: "none",
                fontWeight: 600,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {contactEmail}
            </Link>
          </Typography>
        )}

        {/* Sign-off */}
        <Box sx={{ mt: 6 }}>
          <Typography
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
              color: c.text.secondary,
            }}
          >
            Thank you for your patience,
          </Typography>
          <Typography
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
              fontWeight: 700,
              color: c.text.strong,
              mt: 0.5,
            }}
          >
            Team Regodit
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default MaintenanceScreen;
